import React, { useEffect, useState } from 'react';
import DsButton from '../DsButton';
import DsSwitch from '../DsSwitch';
import DsSelect from '../DsSelect';
import Skeleton from '../Skeleton';
import WorkAvailabilityHelp from './WorkAvailabilityHelp';
import { getAdminResponseStatus, updateAdminResponseStatus, type ResponseStatus } from '../../../api/workAvailability';
import { RESPONSE_STATUS_ICONS, RESPONSE_STATUS_LABELS } from './ResponseStatusProvider';
import '../../../css/common/ResponseStatus.css';

const Button = DsButton as React.ComponentType<any>;
const Switch = DsSwitch as React.ComponentType<any>;
const Select = DsSelect as React.ComponentType<any>;
const LoadingSkeleton = Skeleton as React.ComponentType<{ shape: string; height: number }>;
const modes: ResponseStatus['mode'][] = ['auto', 'available', 'unavailable', 'slow_response', 'off_duty'];

type EditorProps = {
  active?: boolean;
  onSaved?: () => void;
  onOpenSettings?: () => void;
  renderLayout?: (content: React.ReactNode, actions: React.ReactNode) => React.ReactNode;
};

export default function ResponseStatusEditor({ active = true, onSaved, onOpenSettings, renderLayout }: EditorProps) {
  const [status, setStatus] = useState<ResponseStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState('');
  const edit = (patch: Partial<ResponseStatus>) => {
    setStatus(current => current ? { ...current, ...patch } : current);
    setSaved('');
  };
  const load = async () => {
    setError('');
    try { setStatus(await getAdminResponseStatus()); }
    catch { setError('ステータスを読み込めませんでした。読み直してください。'); }
  };
  useEffect(() => {
    if (!active) return undefined;
    setStatus(null);
    setSaved('');
    void load();
    const updated = () => { void load(); };
    window.addEventListener('casticart:response-status-updated', updated);
    return () => window.removeEventListener('casticart:response-status-updated', updated);
  }, [active]);
  const save = async (reset = false) => {
    if (!status || busy) return;
    setBusy(true); setError(''); setSaved('');
    try {
      setStatus(await updateAdminResponseStatus({
        mode: reset ? 'auto' : status.mode, note: reset ? '' : status.note,
        shareNoteWithArtists: !reset && status.shareNoteWithArtists, expectedRevision: status.revision,
      }));
      setSaved(reset ? '当日のステータスをリセットしました。勤務予定は変更していません。' : '今日のステータスを保存しました。');
      onSaved?.();
    } catch (e) { setError(e instanceof Error ? e.message : '保存できませんでした。'); }
    finally { setBusy(false); }
  };
  const actions = <div className="response-status-editor__actions">
    <Button key="save" variant="modify" block disabled={!status} loading={busy} aria-label="今日のステータスを保存" onClick={() => save()}>{renderLayout ? '保存' : 'ステータスを保存'}</Button>
    <Button key="reset" variant="secondary" block disabled={!status || busy} aria-label="ステータスをリセット" onClick={() => save(true)}><i className="fa-solid fa-arrow-rotate-left" aria-hidden="true" /> リセット</Button>
    {onOpenSettings ? <Button key="details" variant="secondary" block className="response-status-editor__settings" aria-label="詳細設定" disabled={busy} onClick={onOpenSettings}><i className="fa-solid fa-sliders" aria-hidden="true" /> 詳細設定</Button> : null}
  </div>;
  const content = <section className="response-status-editor" aria-label="今日のステータス" aria-busy={!status && !error}>
    {status ? <>
      <div className="response-status-editor__choices">
        <div className="work-availability__label-help"><span className="response-status-editor__label">ステータス</span><WorkAvailabilityHelp topic="responseStatus" /></div>
        <Select presentation="modal" modalTitle="ステータスを選択" aria-label="ステータスを変更" value={status.mode} disabled={busy} onChange={(event: React.ChangeEvent<HTMLSelectElement>) => edit({ mode: event.target.value as ResponseStatus['mode'] })}>
          {modes.map(mode => <option key={mode} value={mode}><span className="response-status-editor__selection"><span className="response-status-editor__symbol response-status-symbol" data-response-status={mode} aria-hidden="true"><i className={`fa-solid fa-${RESPONSE_STATUS_ICONS[mode] || 'calendar-days'}`} /></span><span>{RESPONSE_STATUS_LABELS[mode]}</span></span></option>)}
        </Select>
      </div>
      <label className="response-status-editor__note">
        <span className="response-status-editor__note-label"><span>ひとこと <span className="response-status-editor__copy">任意</span></span><span className="response-status-editor__copy" aria-hidden="true">{Array.from(status.note).length} / 80</span></span>
        <input className="response-status-editor__input" aria-label="補足文（任意・80文字まで）" value={status.note} disabled={busy} onChange={event => edit({ note: Array.from(event.target.value).slice(0, 80).join('') })} placeholder="例：リハーサル中のため、返信は明日" />
      </label>
      <div className="response-status-editor__sharing">
        <div className="response-status-editor__sharing-copy">
          <div className="work-availability__label-help"><span className="response-status-editor__label">アーティストにも表示</span><WorkAvailabilityHelp topic="responseSharing" /></div>
          <span className="response-status-editor__copy" role="status" aria-label="ひとことの公開先">{!status.note.trim() ? 'ひとこと未入力' : status.shareNoteWithArtists ? '管理者・アーティスト' : '管理者のみ'}</span>
        </div>
        <Switch checked={status.shareNoteWithArtists} disabled={busy || !status.note.trim()} ariaLabel="補足文をアーティストにも表示" onChange={(checked: boolean) => edit({ shareNoteWithArtists: checked })} />
      </div>
      <div className="response-status-editor__meta"><i className="fa-regular fa-clock" aria-hidden="true" /><span>今日まで</span><WorkAvailabilityHelp topic="responseExpiry" /></div>
    </> : !error ? <><LoadingSkeleton shape="block" height={220} /><LoadingSkeleton shape="block" height={80} /></> : null}
    {error ? <div role="alert"><p className="work-availability__error">{error}</p><Button variant="secondary" disabled={busy} onClick={load}>最新の内容を読み直す</Button></div> : null}
    {saved ? <p role="status" className="response-status-editor__copy">{saved}</p> : null}
  </section>;
  return <>{renderLayout ? renderLayout(content, actions) : <div className="response-status-editor__panel">{content}{actions}</div>}</>;
}
